import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  CalendarX2,
  ChevronLeft,
  ClipboardCheck,
  DatabaseBackup,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import type { Capability } from '@shared/permissions';
import { useAuth } from '@/lib/auth';
import { useFocusTrap } from '@/lib/use-focus-trap';
import schoolLogo from '@/assets/school-logo-sm.png';

export interface SidebarProps {
  /** Controlled mobile drawer state (AppLayout owns this). */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  /** Rendered above the nav: menu toggle lives in the Header for mobile. */
  schoolName?: string;
}

interface NavEntry {
  label: string;
  to: string;
  icon: ReactNode;
  capability?: Capability;
  /** Exact match only (prevents Dashboard from lighting up on every route). */
  end?: boolean;
}

const NAV_ENTRIES: NavEntry[] = [
  { label: 'Dashboard', to: '/', icon: <LayoutDashboard className="h-5 w-5" />, capability: 'dashboard:view', end: true },
  { label: 'Students', to: '/students', icon: <Users className="h-5 w-5" />, capability: 'student:view' },
  { label: 'Fees Structure', to: '/fees-structure', icon: <ListChecks className="h-5 w-5" />, capability: 'fees:view' },
  { label: 'Payments', to: '/payments', icon: <Wallet className="h-5 w-5" />, capability: 'payment:view' },
  { label: 'Reports', to: '/reports', icon: <BarChart3 className="h-5 w-5" />, capability: 'report:view' },
  { label: 'Users', to: '/users', icon: <ShieldCheck className="h-5 w-5" />, capability: 'user:view' },
  { label: 'Pending Approvals', to: '/approvals', icon: <ClipboardCheck className="h-5 w-5" />, capability: 'approval:view' },
  { label: 'Student Accounts', to: '/accounts', icon: <BookOpen className="h-5 w-5" />, capability: 'account:view' },
  { label: 'Audit Log', to: '/audit', icon: <ScrollText className="h-5 w-5" />, capability: 'audit:view' },
  { label: 'School Settings', to: '/settings', icon: <Settings className="h-5 w-5" />, capability: 'settings:view' },
  { label: 'End of Year', to: '/year-end', icon: <CalendarX2 className="h-5 w-5" />, capability: 'year:close' },
  { label: 'Backups', to: '/backups', icon: <DatabaseBackup className="h-5 w-5" />, capability: 'backup:view' },
  { label: 'Change Password', to: '/change-password', icon: <KeyRound className="h-5 w-5" />, end: false },
];

function navItemClass(isActive: boolean, collapsed: boolean): string {
  return [
    'flex items-center gap-3 rounded-lg border-l-[3px] py-2 text-sm font-medium transition-all',
    collapsed ? 'justify-center px-2' : 'px-3',
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-gold-soft',
    isActive
      ? 'border-gold bg-white/10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]'
      : 'border-transparent text-navy-line/85 hover:bg-white/10 hover:text-white',
  ].join(' ');
}

function LogoBand({ collapsed }: { collapsed: boolean }) {
  return (
    <div className={`flex items-center gap-3 border-b border-white/10 px-4 py-5 ${collapsed ? 'justify-center px-2' : ''}`}>
      <img
        src={schoolLogo}
        alt=""
        aria-hidden="true"
        className="h-11 w-11 shrink-0 rounded-full bg-white p-0.5 shadow-sm ring-1 ring-white/15"
      />
      {!collapsed && (
        <span className="min-w-0">
          <span className="block truncate font-display text-sm font-bold tracking-tight text-white">Zvishavane High</span>
          <span className="block truncate text-[11px] font-medium uppercase tracking-widest text-gold-soft">
            Fees Management
          </span>
        </span>
      )}
    </div>
  );
}

function NavList({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { can } = useAuth();

  const entries = NAV_ENTRIES.filter((entry) => !entry.capability || can(entry.capability));

  return (
    <ul className="scroll-area flex-1 space-y-1 overflow-y-auto px-2 py-4">
      {entries.map((entry) => (
        <li key={entry.to}>
          <NavLink
            to={entry.to}
            end={entry.end}
            title={collapsed ? entry.label : undefined}
            onClick={onNavigate}
            className={({ isActive }) => navItemClass(isActive, collapsed)}
          >
            {({ isActive }) => (
              <>
                <span aria-hidden="true" className="shrink-0">{entry.icon}</span>
                {!collapsed && <span className="truncate">{entry.label}</span>}
                {isActive && <span className="sr-only">(current page)</span>}
              </>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function LogoutItem({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async (): Promise<void> => {
    onNavigate?.();
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="border-t border-white/10 px-2 py-3">
      <button
        type="button"
        title={collapsed ? 'Logout' : undefined}
        onClick={() => void handleLogout()}
        className={[
          'flex w-full items-center gap-3 rounded-lg border-l-[3px] border-transparent py-2 text-sm font-medium text-navy-line/85 transition-colors',
          collapsed ? 'justify-center px-2' : 'px-3',
          'hover:bg-state-danger/20 hover:text-white',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-gold-soft',
        ].join(' ')}
      >
        <LogOut aria-hidden="true" className="h-5 w-5 shrink-0" />
        {!collapsed && <span>Logout</span>}
      </button>
    </div>
  );
}

/**
 * Dark steel-blue navigation.
 * - Desktop: fixed column, collapsible to icons (React state).
 * - Mobile: slide-in drawer with focus trap and Escape to close.
 * Active items get a lighter steel background, white text and a pale left
 * accent bar plus aria-current="page".
 */
export function Sidebar({ mobileOpen = false, onMobileClose, schoolName }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);

  useFocusTrap(drawerRef, mobileOpen, onMobileClose);

  const desktop = (
    <aside
      className={`no-print sticky top-0 z-30 hidden h-screen shrink-0 flex-col bg-navy-deep shadow-[2px_0_12px_-4px_rgba(11,31,51,0.45)] md:flex ${
        collapsed ? 'w-16' : 'w-64'
      } transition-[width]`}
      aria-label="Main navigation"
    >
      <div className="on-steel">
        <LogoBand collapsed={collapsed} />
        <nav className="flex min-h-0 flex-1 flex-col" aria-label="Main">
          <NavList collapsed={collapsed} />
          <LogoutItem collapsed={collapsed} />
        </nav>
        <div className="border-t border-white/10 px-2 py-2">
          <button
            type="button"
            onClick={() => setCollapsed((current) => !current)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            className="flex w-full items-center justify-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-navy-line/85 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-gold-soft"
          >
            <ChevronLeft
              aria-hidden="true"
              className={`h-4 w-4 transition-transform ${collapsed ? 'rotate-180' : ''}`}
            />
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </div>
    </aside>
  );

  const drawer = mobileOpen ? (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Navigation menu">
      <div className="absolute inset-0 bg-navy-deep/60" aria-hidden="true" onClick={onMobileClose} />
      <nav
        ref={drawerRef}
        aria-label="Main navigation"
        className="on-steel absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-navy-deep shadow-pop"
      >
        <div className="relative">
          <LogoBand collapsed={false} />
          <button
            type="button"
            aria-label="Close navigation menu"
            onClick={onMobileClose}
            className="absolute right-3 top-4 rounded-md p-1.5 text-navy-line hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold-soft"
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
        {schoolName && (
          <p className="border-b border-white/10 px-4 py-2 text-xs font-medium uppercase tracking-wide text-gold-soft/90">
            {schoolName}
          </p>
        )}
        <nav className="flex min-h-0 flex-1 flex-col" aria-label="Mobile menu">
          <NavList collapsed={false} onNavigate={onMobileClose} />
          <LogoutItem collapsed={false} onNavigate={onMobileClose} />
        </nav>
      </nav>
    </div>
  ) : null;

  return (
    <>
      {desktop}
      {drawer}
    </>
  );
}

/** Mobile menu opener, rendered inside the Header. */
export function SidebarMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open navigation menu"
      className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-steel-light bg-surface text-steel shadow-card hover:bg-steel-light focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-steel md:hidden"
    >
      <Menu aria-hidden="true" className="h-5 w-5" />
    </button>
  );
}
